import React, { FC } from 'react';
import Button from '@mui/material/Button';
import {
  Box,
  IconButton,
  InputAdornment,
  TextareaAutosize,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from '../../../../shared/hooks/redux';
import {
  resetStates,
  setUploadedAttachments,
  setContactPerson,
  setOrderComment,
  setTimerCounter,
} from '../../../../core/redux/massOrderState';
import { PageUnauthenticated } from '../../../../shared/components/page';
import { Header } from '../../../../components/header';
import { Content } from '../../../../shared/components/Content';
import { InnsendingLinje } from '../../../../components/Header4';
import { ChooseContactPerson } from '../../../../components/ContactPerson';
import { CancelButton } from '../../../../shared/components/CancelButton';
import { useDeleteFileMutation, useUploadFilesMutation } from '../../../../core/redux/transport';
import { UploadButton } from '../EgenerklaringPage';
import { FileLink } from '../../../../shared/components/FileLink';
import { UploadedFile } from '../../../../core/types/transport/api/uploadFilesResponse';

export const UtTilleggsdetaljerPage: FC = () => {
  const navigate = useNavigate();
  const {
    timerCounter,
    orderComment,
    vehicleType,
    projectTo,
    cubic,
    typeOfMass,
    contactPerson,
    requiresDeclaration,
    uploadedAttachments,
  } = useSelector((s) => s.massOrderState);
  const dispatch = useDispatch();

  const [uploadFiles, { isLoading: isUploadingFiles }] = useUploadFilesMutation();
  const [deleteFile, { isLoading: isDeletingFile }] = useDeleteFileMutation();

  const handleUploadFiles = async (files: FileList | null) => {
    if (!files) return;
    const uploadedFiles = await uploadFiles(files).unwrap();
    const newFileList = uploadedAttachments
      ? [...uploadedAttachments, ...uploadedFiles]
      : uploadedFiles;
    dispatch(setUploadedAttachments(newFileList));
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    await deleteFile(file.id).unwrap();
    dispatch(setUploadedAttachments(uploadedAttachments?.filter((f) => f.id !== file.id) || []));
  };

  if (
    projectTo === null
    || cubic === null
    || typeOfMass === 0
    || vehicleType.type === 0
  ) {
    navigate('/bestilling');
    return null;
  }

  const updateTimerCounter = (value: string) => {
    if (value) {
      dispatch(setTimerCounter(Number.parseFloat(value)));
    } else {
      dispatch(setTimerCounter(null));
    }
  };

  const disableNextPage = contactPerson === null
    || contactPerson?.name === ''
    || contactPerson?.number === '';

  return (
    <PageUnauthenticated className="tilleggsdetaljer-page parent-pos">
      <Header>Massebestilling</Header>
      <Content>
        <InnsendingLinje
          text={`Steg ${requiresDeclaration ? 4 : 3} - Tilleggsdetaljer`}
          styling="page-item"
        />
        <InnsendingLinje
          text="Ekstra tid i anlegg (valgfritt)"
          styling="page-item"
        />

        <div className="page-item timer-clicker">
          <Button
            className="left-element"
            onClick={() => dispatch(
              setTimerCounter(
                timerCounter && Math.max(timerCounter - 0.5, 0),
              ),
            )}
            variant="outlined"
            size="large"
          >
            <RemoveIcon fontSize="inherit" />
          </Button>
          <TextField
            className="center-element"
            size="small"
            type="number"
            value={timerCounter !== null ? Math.min(24, timerCounter || 0) : ''}
            InputProps={{
              inputProps: { min: 0, max: 24 },
              endAdornment: (
                <InputAdornment position="end">time(r)</InputAdornment>
              ),
            }}
            onChange={(e) => updateTimerCounter(e.target.value)}
            onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
          />
          <Button
            className="right-element"
            onClick={() => dispatch(setTimerCounter(Math.min(24, (timerCounter || 0) + 0.5)))}
            variant="outlined"
            size="small"
          >
            <AddIcon fontSize="inherit" />
          </Button>
        </div>
        <InnsendingLinje
          text="Kontaktperson"
          styling="page-item remove-bottom-padding"
        />
        <div className="page-item">
          <ChooseContactPerson
            onChange={(value) => dispatch(setContactPerson(value))}
            value={contactPerson}
          />
        </div>
        <InnsendingLinje text="Vedlegg (valgfritt)" styling="page-item remove-bottom-padding" />
        <p className="page-item">
          Vedlegg som er nødvendige sjåfør, den som skal motta leveransen eller koordinator.
        </p>
        <div>
          <div className="page-item">
            <UploadButton
              loading={isUploadingFiles}
              text="Last opp vedlegg"
              onFileChange={handleUploadFiles}
              multiple
            />
          </div>
          {uploadedAttachments && uploadedAttachments.length > 0 && (
            <div className="page-item vertical-padding">
              <p>Lastet opp fil(er):</p>
              <ul>
                {uploadedAttachments.map((file) => (
                  <li key={file.id}>
                    <Box display="flex" gap={2} alignItems="center">
                      <FileLink file={file} />
                      <IconButton
                        onClick={() => handleDeleteFile(file)}
                        disabled={isDeletingFile}
                        size="small"
                        color="primary"
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <InnsendingLinje
          text="Er det noe mer vi bør vite? (valgfritt)"
          styling="page-item remove-bottom-padding"
        />
        <p className="page-item">
          Spesielle forhold eller hensyn på anlegget, hvem skal motta leveransen
          eller annen nyttig informasjon som vil hjelp oss å planlegge bedre.
        </p>
        <div className="text-item flex-parent">
          <TextareaAutosize
            minRows={20}
            placeholder="Legg til kommentar"
            value={orderComment}
            className="fill-screen"
            onChange={(e) => dispatch(setOrderComment(e.target.value))}
          />
        </div>

        <div className="bottom-inner">
          <div className="bottom-element">
            <Button
              className="left-element"
              onClick={() => navigate(-1)}
              variant="contained"
              size="large"
            >
              {'<'}
            </Button>
            <CancelButton
              confirm={() => {
                dispatch(resetStates());
                return navigate('/bestilling');
              }}
            />
            <Button
              className="right-element"
              disabled={disableNextPage}
              onClick={() => navigate('/masseut/innsending')}
              variant="contained"
              size="large"
            >
              {'>'}
            </Button>
          </div>
        </div>
      </Content>
    </PageUnauthenticated>
  );
};
